import {
  type XrplTransaction,
  createXrplSourceId,
  xrplClassicAddressSchema,
  xrplTransactionSchema,
} from '@pkg/xrpl'
import { and, eq, sql } from 'drizzle-orm'
import { z } from 'zod'
import { accountXrplSchema } from '../querySchema'
import {
  accountTable,
  xrplSyncCheckpointTable,
  xrplTransactionIndexTable,
} from '../schema'
import type { DatabaseWithTransaction } from '../types'

/** portfolio の開始日時と固定検索範囲に結び付いた再開位置。 */
export const xrplSyncCheckpointSchema = z
  .object({
    startTime: z.number().int().nonnegative().safe(),
    ledgerMin: z.number().int().positive().safe(),
    ledgerMax: z.number().int().positive().safe(),
    marker: z.record(z.unknown()).optional(),
    complete: z.boolean(),
  })
  .refine(
    (checkpoint) =>
      checkpoint.ledgerMin <= checkpoint.ledgerMax &&
      (!checkpoint.complete || checkpoint.marker === undefined),
    'Invalid XRPL checkpoint',
  )

/** 検証済みの再開位置。 */
export type XrplSyncCheckpoint = z.infer<typeof xrplSyncCheckpointSchema>

/**
 * Mainnet の Classic Address を重複なく登録する。
 * @param params DB と登録アドレス一覧
 * @returns 入力順の重複を除いた Account ID
 */
export const registerXrplAccounts = async (params: {
  database: DatabaseWithTransaction
  addresses: readonly string[]
}): Promise<readonly number[]> => {
  const addresses = z
    .array(xrplClassicAddressSchema)
    .nonempty()
    .parse(params.addresses)
  const ids = new Set<number>()
  await params.database.transaction([
    (tx) => [
      (async () => {
        for (const address of addresses) {
          await tx
            .insert(accountTable)
            .values({
              type: 'xrpl',
              category: 'xrpl',
              group: address,
              name: address,
              xrplAddress: address,
              syncing: true,
              xrplIndexComplete: false,
            })
            .onConflictDoNothing({ target: accountTable.xrplAddress })
          const [row] = await tx
            .select({ id: accountTable.id })
            .from(accountTable)
            .where(eq(accountTable.xrplAddress, address))
          if (row === undefined)
            throw new Error('XRPL account registration failed')
          ids.add(row.id)
        }
      })(),
    ],
  ])
  return [...ids]
}

/**
 * 取得済みページを冪等保存する。解析済みの状態は再取得で戻さない。
 * @param params DB、Account ID、検証済み取引一覧
 * @returns ページ全体の保存完了
 */
export const saveXrplSyncPage = async (params: {
  database: DatabaseWithTransaction
  accountId: number
  transactions: readonly XrplTransaction[]
  checkpoint?: XrplSyncCheckpoint
}): Promise<void> => {
  const transactions = z.array(xrplTransactionSchema).parse(params.transactions)
  const checkpoint =
    params.checkpoint === undefined
      ? undefined
      : xrplSyncCheckpointSchema.parse(params.checkpoint)
  if (
    checkpoint !== undefined &&
    transactions.some(
      (transaction) =>
        transaction.ledger_index < checkpoint.ledgerMin ||
        transaction.ledger_index > checkpoint.ledgerMax,
    )
  )
    throw new Error('XRPL transaction is outside checkpoint range')
  await params.database.transaction([
    (tx) => [
      (async () => {
        const [row] = await tx
          .select()
          .from(accountTable)
          .where(eq(accountTable.id, params.accountId))
        accountXrplSchema.parse(row)
        if (checkpoint !== undefined) {
          const [previous] = await tx
            .select()
            .from(xrplSyncCheckpointTable)
            .where(eq(xrplSyncCheckpointTable.accountId, params.accountId))
          if (
            previous !== undefined &&
            previous.startTime === checkpoint.startTime
          ) {
            if (
              !previous.complete &&
              (previous.ledgerMin !== checkpoint.ledgerMin ||
                previous.ledgerMax !== checkpoint.ledgerMax)
            )
              throw new Error('XRPL incomplete range must be resumed')
            if (
              previous.complete &&
              (checkpoint.ledgerMin > previous.ledgerMax + 1 ||
                checkpoint.ledgerMax < previous.ledgerMax)
            )
              throw new Error('XRPL checkpoint would skip ledger history')
          }
          const values = {
            accountId: params.accountId,
            ...checkpoint,
            marker:
              checkpoint.marker === undefined
                ? null
                : JSON.stringify(checkpoint.marker),
          }
          await tx
            .insert(xrplSyncCheckpointTable)
            .values(values)
            .onConflictDoUpdate({
              target: xrplSyncCheckpointTable.accountId,
              set: values,
            })
        }
        for (const transaction of transactions) {
          await tx
            .insert(xrplTransactionIndexTable)
            .values({
              accountId: params.accountId,
              sourceId: createXrplSourceId({ hash: transaction.hash }),
              hash: transaction.hash,
              timestamp: new Date(transaction.close_time_iso),
              ledgerIndex: transaction.ledger_index,
              raw: JSON.stringify(transaction),
              analyzed: false,
            })
            .onConflictDoUpdate({
              target: [
                xrplTransactionIndexTable.accountId,
                xrplTransactionIndexTable.sourceId,
              ],
              set: {
                raw: JSON.stringify(transaction),
                timestamp: new Date(transaction.close_time_iso),
                ledgerIndex: transaction.ledger_index,
              },
              setWhere: eq(xrplTransactionIndexTable.analyzed, false),
            })
        }
        await tx
          .update(accountTable)
          .set({ syncing: true, xrplIndexComplete: false })
          .where(eq(accountTable.id, params.accountId))
      })(),
    ],
  ])
}

/**
 * XRPL の未解析取引を時系列で取得する。
 * @param params DB と Account ID
 * @returns 検証済みアカウントと未解析取引
 */
export const getXrplSyncState = async (params: {
  database: DatabaseWithTransaction
  accountId: number
}) => {
  const db = params.database.database
  const [row] = await db
    .select()
    .from(accountTable)
    .where(eq(accountTable.id, params.accountId))
  const account = accountXrplSchema.parse(row)
  const pending = await db
    .select()
    .from(xrplTransactionIndexTable)
    .where(
      and(
        eq(xrplTransactionIndexTable.accountId, params.accountId),
        eq(xrplTransactionIndexTable.analyzed, false),
      ),
    )
    .orderBy(
      xrplTransactionIndexTable.timestamp,
      xrplTransactionIndexTable.sourceId,
    )
  const [checkpointRow] = await db
    .select()
    .from(xrplSyncCheckpointTable)
    .where(eq(xrplSyncCheckpointTable.accountId, params.accountId))
  const checkpoint =
    checkpointRow === undefined
      ? undefined
      : xrplSyncCheckpointSchema.parse({
          ...checkpointRow,
          marker:
            checkpointRow.marker === null
              ? undefined
              : JSON.parse(checkpointRow.marker),
        })
  return { account, pending, checkpoint }
}

/**
 * 履歴取得完了と資産解析完了を区別して同期状態を保存する。
 * @param params DB、Account ID、必要範囲の取得完了
 * @returns 状態保存の完了
 */
export const setXrplIndexComplete = async (params: {
  database: DatabaseWithTransaction
  accountId: number
  complete: boolean
}): Promise<void> => {
  await params.database.database
    .update(accountTable)
    .set({
      xrplIndexComplete: params.complete,
      ...(params.complete ? { lastSyncedAt: new Date() } : {}),
      syncing: params.complete
        ? sql`EXISTS (SELECT 1 FROM ${xrplTransactionIndexTable} WHERE ${xrplTransactionIndexTable.accountId} = ${params.accountId} AND ${xrplTransactionIndexTable.analyzed} = 0)`
        : true,
    })
    .where(
      and(eq(accountTable.id, params.accountId), eq(accountTable.type, 'xrpl')),
    )
}

/**
 * 生履歴を保持したまま全履歴の再取得・再解析を要求する。
 * @param params DB と Account ID
 * @returns 再同期要求の保存完了
 */
export const resetXrplAccountSync = async (params: {
  database: DatabaseWithTransaction
  accountId: number
}): Promise<void> => {
  await params.database.transaction([
    (tx) => [
      (async () => {
        const [row] = await tx
          .select()
          .from(accountTable)
          .where(eq(accountTable.id, params.accountId))
        accountXrplSchema.parse(row)
        await tx
          .delete(xrplSyncCheckpointTable)
          .where(eq(xrplSyncCheckpointTable.accountId, params.accountId))
        await tx
          .update(xrplTransactionIndexTable)
          .set({ analyzed: false })
          .where(eq(xrplTransactionIndexTable.accountId, params.accountId))
        await tx
          .update(accountTable)
          .set({ syncing: true, xrplIndexComplete: false, lastSyncedAt: null })
          .where(eq(accountTable.id, params.accountId))
      })(),
    ],
  ])
}
